"use client";

/**
 * Page de réservation Doctolib-style — multi-étapes sur page dédiée.
 * Layout : main 2/3 (steps), sidebar 1/3 (médecin + récap progressif).
 *
 * Steps :
 *   1. Pour qui ? (moi / proche)
 *   2. Motif de consultation (depuis /api/physicians/[slug]/booking-config)
 *   3. Date & créneau (depuis /api/availabilities)
 *   4. Identification + confirmation (timer 15min)
 *
 * À la confirmation : POST /api/appointments → status PENDING + écrit
 * localStorage 'sahha:pending-rdv' pour la PendingAppointmentBanner.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { CaretDown, CaretLeft, MapPin, Calendar, Clock, User, Stethoscope, Check } from "@phosphor-icons/react/dist/ssr";
import { BookingAuthGate } from "@/components/booking/BookingAuthGate";

type ConsultationType = "IN_OFFICE" | "VIDEO" | "HOME_VISIT" | "PHONE" | "URGENT";
type Slot = { startAt: string; endAt: string; type: ConsultationType };
type Reason = {
  id: string;
  label: string;
  durationMinutes: number;
  type: "IN_OFFICE" | "VIDEO";
  priceMad?: number;
};

type Props = {
  physicianSlug: string;
  physicianName: string;
  physicianTitle: string;
  physicianSpecialty?: string;
  physicianPhotoUrl?: string | null;
  physicianInitials: string;
  cabinetAddress?: string;
  city?: string;
  userProfile?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null;
  isAuthenticated?: boolean;
};

type Step = "motif" | "date" | "auth" | "confirm" | "success";

function bcp47(locale: string): string {
  if (locale === "fr") return "fr-FR";
  if (locale === "ar") return "ar-MA";
  return "en-GB";
}

function formatDay(d: Date, bcp: string): string {
  return d.toLocaleDateString(bcp, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
function formatTime(iso: string, bcp: string): string {
  return new Date(iso).toLocaleTimeString(bcp, { hour: "2-digit", minute: "2-digit" });
}


export function BookingPageClient(props: Props) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("booking.toasts");
  const tFlow = useTranslations("booking.flow");
  const bcp = bcp47(locale);

  const [step, setStep] = useState<Step>("motif");

  const [reasons, setReasons] = useState<Reason[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [cfgError, setCfgError] = useState(false);
  const [slotsError, setSlotsError] = useState(false);

  const [selectedReason, setSelectedReason] = useState<Reason | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [showAllDays, setShowAllDays] = useState(false);

  const [firstName, setFirstName] = useState(props.userProfile?.firstName ?? "");
  const [lastName, setLastName] = useState(props.userProfile?.lastName ?? "");
  const [phone, setPhone] = useState(props.userProfile?.phone ?? "");
  const [email, setEmail] = useState(props.userProfile?.email ?? "");
  const [alreadyConsulted, setAlreadyConsulted] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ─── Hold 15 min (anti-double-booking) ────────────────────────────
  // Posé dès qu'on arrive au step "auth" ou "confirm". Le serveur réserve
  // le créneau pour ce patient + bloque les autres patients web ET le
  // médecin desktop (409 si tentative concurrent). Libéré à la fermeture
  // (beforeunload), à la confirmation (consume), ou à l'expiration 15 min.
  const [hold, setHold] = useState<{ id: string; token: string; expiresAt: number } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!hold) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold?.id]);
  const secondsLeft = useMemo(() => {
    if (!hold) return 15 * 60;
    return Math.max(0, Math.ceil((hold.expiresAt - Date.now()) / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold, tick]);
  const minutesLeft = Math.ceil(secondsLeft / 60);

  // Si le hold expire pendant que le patient remplit le form → le serveur
  // refusera l'appointment au submit. On informe le user pour qu'il refresh.
  useEffect(() => {
    if (hold && secondsLeft === 0) {
      toast.error(t("expired"));
      setHold(null);
      setSelectedSlot(null);
      setStep("date");
      // refresh slots pour refléter la libération
      void loadSlots(selectedReason?.durationMinutes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, hold]);

  // Libère le hold via beacon (survit à la fermeture d'onglet).
  function releaseHold(h: { id: string; token: string }) {
    const url = `/api/appointments/hold/${encodeURIComponent(h.id)}?token=${encodeURIComponent(h.token)}`;
    try { fetch(url, { method: "DELETE", keepalive: true }).catch(() => {}); } catch {}
  }
  useEffect(() => {
    if (!hold) return;
    const onUnload = () => releaseHold(hold);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      releaseHold(hold);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hold?.id]);

  // ─── Chargement slots avec durée du motif ─────────────────────────
  // Le serveur filtre les slots qui n'ont pas `durationMinutes` libres
  // consécutifs (anti-conflit). Re-fetch à chaque changement de motif.
  const loadSlots = useCallback(async (requiredDuration?: number) => {
    setSlotsError(false);
    try {
      const durParam = requiredDuration ? `&durationMinutes=${requiredDuration}` : "";
      const res = await fetch(`/api/availabilities?physicianSlug=${encodeURIComponent(props.physicianSlug)}&days=14${durParam}`);
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error("availabilities-error");
      setSlots(data.slots ?? []);
    } catch {
      setSlotsError(true);
      toast.error(t("networkError"));
    }
  }, [props.physicianSlug, t]);

  // Charger la config (motifs de consultation) — distingue l'échec réseau
  // du cas légitime "aucun motif configuré" (cfgError vs reasons.length === 0).
  const loadConfig = useCallback(async () => {
    setLoadingCfg(true);
    setCfgError(false);
    try {
      const res = await fetch(`/api/physicians/${encodeURIComponent(props.physicianSlug)}/booking-config`);
      const cfg = await res.json();
      if (!res.ok || cfg?.error) throw new Error("booking-config-error");
      setReasons(cfg?.reasons ?? []);
    } catch {
      setCfgError(true);
      toast.error(t("networkError"));
    } finally {
      setLoadingCfg(false);
    }
  }, [props.physicianSlug, t]);

  // Charger config initiale (motifs + slots sans filtre durée)
  useEffect(() => {
    void loadConfig();
    void loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.physicianSlug]);

  // Re-fetch slots quand le motif change → durée appliquée
  useEffect(() => {
    if (!selectedReason) return;
    loadSlots(selectedReason.durationMinutes);
  }, [selectedReason?.id, selectedReason?.durationMinutes, loadSlots]);

  // Slots filtrés par type ET non-passés (sécurité client si page ouverte longtemps)
  const filteredSlots = useMemo(() => {
    if (!selectedReason) return [];
    const now = Date.now();
    return slots
      .filter((s) => s.type === selectedReason.type)
      .filter((s) => new Date(s.startAt).getTime() > now);
  }, [slots, selectedReason]);

  // Group par jour
  const slotsByDay = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of filteredSlots) {
      const d = new Date(s.startAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(s);
    }
    return m;
  }, [filteredSlots]);

  // Liste de jours à afficher (5 par défaut, "voir plus" → 14)
  const displayedDays = useMemo(() => {
    const days: { key: string; date: Date; slots: Slot[] }[] = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const limit = showAllDays ? 14 : 5;
    for (let i = 0; i < 14; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const daySlots = slotsByDay.get(key) ?? [];
      if (daySlots.length === 0) continue;
      days.push({ key, date: d, slots: daySlots });
      if (days.length >= limit) break;
    }
    return days;
  }, [slotsByDay, showAllDays]);

  function goNext(next: Step) {
    setStep(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /**
   * Pose un hold 15min sur le créneau sélectionné AVANT de passer à l'étape
   * auth/confirm. Si conflit (autre patient ou médecin sur le même créneau),
   * remonte l'erreur au user et reload les slots dispo.
   * Le slot est passé en argument (état React pas encore propagé au call).
   */
  async function afterSlotPicked(slot: Slot) {
    if (!selectedReason) return;
    setSelectedSlot(slot);
    // Libère un hold précédent si existe (re-sélection)
    if (hold) { releaseHold(hold); setHold(null); }
    try {
      const res = await fetch("/api/appointments/hold", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          physicianSlug: props.physicianSlug,
          scheduledAt: slot.startAt,
          durationMinutes: selectedReason.durationMinutes,
          type: slot.type,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        const msg = data.message || t("slotUnavailable");
        toast.error(msg);
        await loadSlots(selectedReason.durationMinutes);
        setSelectedSlot(null);
        return;
      }
      setHold({ id: data.holdId, token: data.holdToken, expiresAt: new Date(data.expiresAt).getTime() });
      // Step suivant : auth si anon, sinon confirm
      if (props.isAuthenticated) goNext("confirm");
      else goNext("auth");
    } catch {
      toast.error(t("networkError"));
    }
  }

  async function submit() {
    if (!selectedSlot || !selectedReason) return;
    if (!firstName.trim() || !lastName.trim() || !/^\+?\d[\d\s-]{7,}$/.test(phone.trim())) {
      toast.error(t("missingFields"));
      return;
    }
    // Garde-fou : refuse de submit si le créneau est dans le passé (page restée
    // ouverte trop longtemps). Le serveur refuse aussi mais autant donner
    // une erreur claire côté UX.
    if (new Date(selectedSlot.startAt).getTime() < Date.now()) {
      toast.error(t("slotPast"));
      setHold(null);
      setSelectedSlot(null);
      setStep("date");
      void loadSlots(selectedReason.durationMinutes);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/appointments", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          physicianSlug: props.physicianSlug,
          scheduledAt: selectedSlot.startAt,
          type: selectedSlot.type,
          durationMinutes: selectedReason.durationMinutes,
          patientFirstName: firstName.trim(),
          patientLastName: lastName.trim(),
          patientPhone: phone.trim(),
          patientEmail: email.trim() || undefined,
          reason: selectedReason.label,
          isFirstVisit: alreadyConsulted === false,
          // Référence vers ConsultationReason SQL (anti-conflit + stats)
          consultationReasonId: selectedReason.id,
          // Token du hold pour consume atomique (evite double-réservation)
          holdToken: hold?.token,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message ?? t("bookingFailed"));
        return;
      }
      // Hold consumé par le serveur dans la transaction → clear local
      setHold(null);
      // Sauvegarde dans localStorage pour la top banner
      try {
        localStorage.setItem("sahha:pending-rdv", JSON.stringify({
          id: data.appointment.id,
          code: data.appointment.confirmationCode,
          scheduledAt: selectedSlot.startAt,
          physicianSlug: props.physicianSlug,
          physicianName: props.physicianName,
          physicianTitle: props.physicianTitle,
          physicianSpecialty: props.physicianSpecialty,
          physicianPhotoUrl: props.physicianPhotoUrl ?? null,
          motifLabel: selectedReason.label,
          createdAt: Date.now(),
        }));
      } catch {/* noop */}
      setStep("success");
    } catch {
      toast.error(t("networkError"));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Render ────────────────────────────────────────────────
  if (step === "success") {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check size={32} weight="bold" />
        </div>
        <h2 className="text-xl font-bold text-[var(--sahha-text-900)]">{tFlow("success.title")}</h2>
        <p className="mt-2 text-sm text-[var(--sahha-text-600)]">
          {tFlow.rich("success.subtitle", { strong: (chunks) => <strong>{chunks}</strong> })}
        </p>
        <div className="mt-6 grid gap-2 rounded-2xl bg-[var(--sahha-surface-1)] p-4 text-left text-[13px]">
          <Recap icon={<User size={14} />} label={selectedSlot ? `${firstName} ${lastName}` : ""} />
          <Recap icon={<Calendar size={14} />} label={selectedSlot ? formatDay(new Date(selectedSlot.startAt), bcp) : ""} />
          <Recap icon={<Clock size={14} />} label={selectedSlot ? formatTime(selectedSlot.startAt, bcp) : ""} />
          {props.cabinetAddress && <Recap icon={<MapPin size={14} />} label={props.cabinetAddress} />}
          <Recap icon={<Stethoscope size={14} />} label={selectedReason?.label ?? ""} />
        </div>
        <button
          onClick={() => router.push(`/medecin/${props.physicianSlug}` as never)}
          className="mt-6 inline-flex h-12 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] px-8 text-sm font-bold uppercase tracking-wider text-white"
        >
          {tFlow("backToProfile")}
        </button>
      </div>
    );
  }

  function backStep(): Step | null {
    if (step === "date") return "motif";
    if (step === "auth") return "date";
    if (step === "confirm") return props.isAuthenticated ? "date" : "auth";
    return null;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* ─── Main ─── */}
      <div className="rounded-3xl border border-[var(--sahha-border-default)] bg-white p-6 sm:p-8">
        {/* Back */}
        {step !== "motif" && backStep() && (
          <button
            type="button"
            onClick={() => goNext(backStep()!)}
            className="mb-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]"
          >
            <CaretLeft size={14} weight="bold" /> {tFlow("back")}
          </button>
        )}

        {step === "motif" && (
          <div>
            <h2 className="text-xl font-bold text-[var(--sahha-text-900)]">{tFlow("motif.title")}</h2>
            {loadingCfg ? (
              <div className="mt-5 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-[var(--sahha-surface-1)]" />)}
              </div>
            ) : cfgError ? (
              <div className="mt-5 rounded-xl border border-dashed border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
                <p>{t("networkError")}</p>
                <button
                  type="button"
                  onClick={() => void loadConfig()}
                  className="mt-3 rounded-full border border-red-300 px-4 py-1.5 text-[13px] font-semibold text-red-700 hover:bg-red-100"
                >
                  {tFlow("retry")}
                </button>
              </div>
            ) : reasons.length === 0 ? (
              <p className="mt-5 rounded-xl bg-[var(--sahha-surface-1)] p-4 text-sm text-[var(--sahha-text-600)]">{tFlow("motif.empty")}</p>
            ) : (
              <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--sahha-border-default)]">
                {reasons.map((r, i) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => { setSelectedReason(r); goNext("date"); }}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-[var(--sahha-primary-50)] ${i > 0 ? "border-t border-[var(--sahha-border-default)]" : ""}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--sahha-text-900)]">{r.label}</p>
                      <p className="mt-0.5 text-xs text-[var(--sahha-text-500)]">
                        {r.durationMinutes} min{r.priceMad ? ` · ${r.priceMad} MAD` : ""}{r.type === "VIDEO" ? " · Téléconsultation" : ""}
                      </p>
                    </div>
                    <span className="shrink-0 text-[var(--sahha-text-400)]">→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "date" && (
          <div>
            <h2 className="text-xl font-bold text-[var(--sahha-text-900)]">Choisissez la date de consultation</h2>
            {filteredSlots.length === 0 ? (
              <div className="mt-5 rounded-xl border border-dashed border-[var(--sahha-border-default)] bg-[var(--sahha-surface-1)] p-6 text-center text-sm text-[var(--sahha-text-600)]">
                {slotsError ? (
                  <>
                    <p>{t("networkError")}</p>
                    <button
                      type="button"
                      onClick={() => void loadSlots(selectedReason?.durationMinutes)}
                      className="mt-3 rounded-full border border-[var(--sahha-border-default)] px-4 py-1.5 text-[13px] font-semibold text-[var(--sahha-text-700)] hover:bg-white"
                    >
                      {tFlow("retry")}
                    </button>
                  </>
                ) : (
                  "Aucun créneau disponible sur les 14 prochains jours pour ce motif."
                )}
              </div>
            ) : (
              <>
                <div className="mt-5 space-y-2">
                  {displayedDays.map((d) => {
                    const isOpen = openDay === d.key;
                    return (
                      <div key={d.key} className={`overflow-hidden rounded-xl border transition-colors ${isOpen ? "border-[var(--sahha-primary-400)] bg-[var(--sahha-primary-50)]/30" : "border-[var(--sahha-border-default)] bg-white"}`}>
                        <button
                          type="button"
                          onClick={() => setOpenDay(isOpen ? null : d.key)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                        >
                          <span className="font-bold capitalize text-[var(--sahha-text-900)]">{formatDay(d.date, bcp)}</span>
                          <CaretDown size={16} weight="bold" className={`shrink-0 text-[var(--sahha-text-500)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                          <div className="border-t border-[var(--sahha-border-default)] bg-white px-4 py-3">
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                              {d.slots.map((s) => (
                                <button
                                  key={s.startAt}
                                  type="button"
                                  onClick={() => afterSlotPicked(s)}
                                  className="rounded-lg bg-[var(--sahha-primary-50)] py-2 text-[13px] font-semibold text-[var(--sahha-primary-700)] transition hover:bg-[var(--sahha-primary-100)]"
                                >
                                  {formatTime(s.startAt, bcp)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!showAllDays && displayedDays.length >= 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllDays(true)}
                    className="mt-4 w-full rounded-xl border-2 border-[var(--sahha-primary-600)] py-2.5 text-[13px] font-bold uppercase tracking-wider text-[var(--sahha-primary-700)] hover:bg-[var(--sahha-primary-50)]"
                  >
                    Voir plus de dates
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {step === "auth" && (
          <BookingAuthGate
            minutesLeft={minutesLeft}
            onAuthSuccess={async () => {
              // Re-fetch user profile + go to confirm
              try {
                const res = await fetch("/api/me/profile", { credentials: "include" });
                if (res.ok) {
                  const data = await res.json();
                  if (data?.user) {
                    setFirstName(data.user.firstName ?? "");
                    setLastName(data.user.lastName ?? "");
                    setEmail(data.user.email ?? "");
                    setPhone(data.user.phone ?? "");
                  }
                }
              } catch {/* noop */}
              goNext("confirm");
            }}
          />
        )}

        {step === "confirm" && (
          <div>
            <div className="mb-4 rounded-xl bg-[var(--sahha-primary-50)] px-4 py-2 text-center text-[12px] font-semibold text-[var(--sahha-primary-700)]">
              {tFlow("slot.reserved", { minutes: minutesLeft })}
            </div>
            <h2 className="text-xl font-bold text-[var(--sahha-text-900)]">{tFlow("confirm.title")}</h2>
            <div className="mt-5 space-y-3 rounded-2xl border border-[var(--sahha-border-default)] bg-white p-4">
              <Recap icon={<User size={16} />} label={`Pour ${firstName || "vous"} ${lastName || ""}`} />
              <Recap icon={<Calendar size={16} />} label={selectedSlot ? `${formatDay(new Date(selectedSlot.startAt), bcp)} · ${formatTime(selectedSlot.startAt, bcp)}` : ""} />
              {props.cabinetAddress && <Recap icon={<MapPin size={16} />} label={`${props.cabinetAddress}${props.city ? `, ${props.city}` : ""}`} />}
              <Recap icon={<Stethoscope size={16} />} label={selectedReason?.label ?? ""} />
            </div>

            <div className="mt-6 rounded-2xl border border-[var(--sahha-border-default)] bg-white p-4">
              <h3 className="font-bold text-[var(--sahha-text-900)]">{tFlow("consulted.title")}</h3>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setAlreadyConsulted(true)}
                  className={`flex-1 rounded-xl border-2 py-2.5 text-[13px] font-semibold transition ${alreadyConsulted === true ? "border-[var(--sahha-primary-600)] bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)]" : "border-[var(--sahha-border-default)] bg-white"}`}
                >
                  {tFlow("consulted.yes")}
                </button>
                <button
                  type="button"
                  onClick={() => setAlreadyConsulted(false)}
                  className={`flex-1 rounded-xl border-2 py-2.5 text-[13px] font-semibold transition ${alreadyConsulted === false ? "border-[var(--sahha-primary-600)] bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-700)]" : "border-[var(--sahha-border-default)] bg-white"}`}
                >
                  {tFlow("consulted.no")}
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-3">
              <h3 className="font-bold text-[var(--sahha-text-900)]">{tFlow("coords.title")}</h3>
              <div className="grid grid-cols-2 gap-3">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={tFlow("coords.firstName")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-sm" />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={tFlow("coords.lastName")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-sm" />
              </div>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder={tFlow("coords.phone")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-sm" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder={tFlow("coords.email")} className="h-12 rounded-xl border border-[var(--sahha-border-default)] bg-white px-4 text-sm" />
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={submitting || alreadyConsulted === null}
              className="mt-6 inline-flex h-13 w-full items-center justify-center rounded-full bg-[var(--sahha-primary-600)] py-3.5 text-[14px] font-bold uppercase tracking-wider text-white disabled:opacity-60"
            >
              {submitting ? tFlow("confirm.submitting") : tFlow("confirm.cta")}
            </button>
            <p className="mt-3 text-center text-[11px] text-[var(--sahha-text-500)]">
              {tFlow("cancelNotice")}
            </p>
          </div>
        )}
      </div>

      {/* ─── Sidebar ─── */}
      <aside className="space-y-4">
        {/* Carte médecin */}
        <div className="rounded-3xl border border-[var(--sahha-border-default)] bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[var(--sahha-primary-100)] text-base font-bold text-[var(--sahha-primary-700)]">
              {props.physicianPhotoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={props.physicianPhotoUrl} alt={props.physicianName} className="h-full w-full object-cover" />
              ) : (
                props.physicianInitials
              )}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-[var(--sahha-text-900)]">{props.physicianTitle} {props.physicianName}</p>
              {props.physicianSpecialty && <p className="text-[13px] text-[var(--sahha-text-600)]">{props.physicianSpecialty}</p>}
            </div>
          </div>
        </div>

        {/* Recap progressif */}
        {(selectedReason || selectedSlot || (firstName && lastName)) && (
          <div className="rounded-3xl border border-[var(--sahha-border-default)] bg-white p-5">
            <h3 className="font-bold text-[var(--sahha-text-900)]">{tFlow("recap.title")}</h3>
            <ul className="mt-3 space-y-2 text-[13px]">
              {firstName && lastName && (
                <li className="flex items-center gap-2 text-[var(--sahha-text-700)]">
                  <Check size={14} weight="bold" className="shrink-0 text-emerald-600" />
                  Pour {firstName} {lastName}
                </li>
              )}
              {props.cabinetAddress && (
                <li className="flex items-center gap-2 text-[var(--sahha-text-700)]">
                  <Check size={14} weight="bold" className="shrink-0 text-emerald-600" />
                  {props.cabinetAddress}{props.city ? `, ${props.city}` : ""}
                </li>
              )}
              {selectedReason && (
                <li className="flex items-center gap-2 text-[var(--sahha-text-700)]">
                  <Check size={14} weight="bold" className="shrink-0 text-emerald-600" />
                  {selectedReason.label}
                </li>
              )}
              {selectedSlot && (
                <li className="flex items-center gap-2 text-[var(--sahha-text-700)]">
                  <Check size={14} weight="bold" className="shrink-0 text-emerald-600" />
                  {formatDay(new Date(selectedSlot.startAt), bcp)} · {formatTime(selectedSlot.startAt, bcp)}
                </li>
              )}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function Recap({ icon, label }: { icon: React.ReactNode; label: string }) {
  if (!label) return null;
  return (
    <div className="flex items-center gap-2 text-[13px] text-[var(--sahha-text-700)]">
      <span className="shrink-0 text-[var(--sahha-text-500)]">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}
